import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Tooltip } from '@plerionhq/design-system';
import { PleriLogo } from '../PleriLogo';
import { SidebarSection } from './SidebarSection';
import { getSectionsByPosition } from './sidebar-config';
import { useChatStore } from '../../store/chatStore';
import { usePleriRender } from '../../context/PleriRenderContext';
import { PleriClient } from '../../client/PleriClient';
import { DocsChatInformationCard } from './ChatInformationCards';

import { useTranslation } from 'react-i18next';

interface SidebarTriggerProps {
  onClick: () => void;
  onItemClick?: (itemId: string) => void;
  showOnlyChatOptions?: boolean; // Control what to show in trigger
}

export const SidebarTrigger: React.FC<SidebarTriggerProps> = ({
  onClick,
  onItemClick,
  showOnlyChatOptions = true, // Default to only showing chat options
}) => {
  const { t } = useTranslation('pleri/chat');
  const navigate = useNavigate();
  const createConversation = useChatStore((state) => state.createConversation);
  const { isOpen } = usePleriRender();

  // Get sections for mini view (same as MiniSidebar)
  let topSections = getSectionsByPosition('top', 'mini');
  let bottomSections = getSectionsByPosition('bottom', 'mini');

  // Filter to only show new-chat and recent-chats if showOnlyChatOptions is true
  if (showOnlyChatOptions) {
    topSections = topSections
      .map((section) => ({
        ...section,
        items: section.items.filter(
          (item) => item.id === 'new-chat' || item.id === 'recent-chats',
        ),
      }))
      .filter((section) => section.items.length > 0); // Remove empty sections
    bottomSections = []; // Hide bottom sections when showing only chat options
  }

  // Handle new chat creation
  const handleNewChat = () => {
    const newId = createConversation();
    navigate({
      to: '/c/$conversationId',
      params: { conversationId: newId },
    });
    onItemClick?.('new-chat');
  };

  // Handle support options
  const handleChatWithDocs = () => {
    PleriClient.updateChatInput({
      prompt:
        "Hi Pleri, can you help me understand how a feature works? I'd love to learn more about it using the documentation.",
      useExistingConversation: false,
      autoSubmit: true,
      chatInformation: <DocsChatInformationCard />,
    });
    onItemClick?.('chat-with-docs');
  };

  const handleFeatureRequest = () => {
    PleriClient.updateChatInput({
      prompt:
        'Hi Pleri, I think you could benefit from this feature. Can you help me submit a feature request?',
      useExistingConversation: false,
      autoSubmit: true,
    });
    onItemClick?.('feature-request');
  };

  const handleContactSupport = () => {
    PleriClient.updateChatInput({
      prompt:
        'Hi Pleri, I encountered an issue with your capability. Can you help me raise a support ticket?',
      useExistingConversation: false,
      autoSubmit: true,
    });
    onItemClick?.('contact-support');
  };

  // Custom callbacks for specific items
  const itemCallbacks = {
    'new-chat': handleNewChat,
    'chat-with-docs': handleChatWithDocs,
    'feature-request': handleFeatureRequest,
    'contact-support': handleContactSupport,
  };

  return (
    <div className="group relative flex h-full w-[52px] flex-col overflow-hidden border-l border-gray-200/80 bg-gray-50 backdrop-blur-sm dark:border-gray-700/80 dark:bg-gray-800">
      {/* Header - Pleri Logo/Toggle - matches MiniSidebar */}
      <div className="flex items-center px-2 py-2">
        <div className="flex w-full justify-center">
          <div>
            <Tooltip
              label={isOpen ? t('dismissPleriTitle') : t('openPleri')}
              placement="right"
            >
              <button
                onClick={() => {
                  onClick();
                }}
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[4.5px] text-gray-500 transition-all duration-200 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                aria-label={isOpen ? t('dismissPleriTitle') : t('openPleri')}
              >
                <PleriLogo size={32} className="rounded-md" />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
      {/* Top Section - Quick Actions (New Chat, Recent Chats, etc.) */}
      <div className="scrollbar-hide flex flex-1 flex-col gap-1 overflow-y-auto px-0 py-2">
        {topSections.map((section) => (
          <SidebarSection
            key={section.id}
            section={section}
            isCollapsed={true}
            onItemClick={onItemClick}
            itemCallbacks={itemCallbacks}
          />
        ))}
      </div>
      {/* Bottom Section - Settings & Support */}
      {bottomSections.length > 0 && (
        <div className="flex-shrink-0 border-t border-gray-200/80 px-0 py-2 dark:border-gray-700/80">
          {bottomSections.map((section) => (
            <SidebarSection
              key={section.id}
              section={section}
              isCollapsed={true}
              onItemClick={onItemClick}
              itemCallbacks={itemCallbacks}
            />
          ))}
        </div>
      )}
    </div>
  );
};
